'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  PREVIEW_ANNOTATION_COLOR,
  PREVIEW_ANNOTATION_MAX_BASE64,
  PREVIEW_ANNOTATION_MAX_POINTS,
  PREVIEW_ANNOTATION_MAX_STROKES,
  parsePreviewAnnotationContext,
  parsePreviewSnapshot,
  previewAnnotationTargetsEqual,
  type PreviewAnnotationStroke,
  type PreviewAnnotationTarget,
  type PreviewSnapshot,
} from '@/lib/preview/annotation';
import type { ImageAttachment } from '@/lib/research-assistant/types';
import {
  loadPreviewAnnotationRenderer,
  type PreviewAnnotationCaptureRequest,
  type PreviewAnnotationCaptureResult,
} from './annotation-bridge';
import {
  usePreviewAnnotationAwareness,
  type PreviewAnnotationDraft,
} from './PreviewAnnotationContext';

async function renderImages(draft: PreviewAnnotationDraft): Promise<ImageAttachment[]> {
  const image = new Image();
  image.src = `data:image/jpeg;base64,${draft.snapshot.base64Data}`;
  await image.decode();
  const canvas = document.createElement('canvas');
  canvas.width = draft.snapshot.width;
  canvas.height = draft.snapshot.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Drawing is unavailable in this browser.');
  ctx.drawImage(image, 0, 0);
  const scale = canvas.width / draft.snapshot.viewport.width;
  for (const stroke of draft.strokes) {
    ctx.strokeStyle = stroke.color;
    ctx.fillStyle = stroke.color;
    ctx.lineWidth = stroke.width * scale;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    stroke.points.forEach((point, index) => {
      const x = point.x * canvas.width;
      const y = point.y * canvas.height;
      if (index === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    if (stroke.points.length === 1) {
      const point = stroke.points[0];
      ctx.arc(point.x * canvas.width, point.y * canvas.height, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
    } else ctx.stroke();
  }
  let base64Data = '';
  for (const quality of [0.86, 0.72, 0.58, 0.42, 0.28, 0.16]) {
    base64Data = canvas.toDataURL('image/jpeg', quality).split(',')[1];
    if (base64Data.length <= PREVIEW_ANNOTATION_MAX_BASE64) break;
  }
  if (base64Data.length > PREVIEW_ANNOTATION_MAX_BASE64)
    throw new Error('This drawing is too large. Clear it and try fewer marks.');
  const markedImageId = `${draft.id}-marked`;
  const previewAnnotation = parsePreviewAnnotationContext({
    draftId: draft.id,
    target: draft.target,
    capturedAt: draft.snapshot.capturedAt,
    viewport: draft.snapshot.viewport,
    elements: draft.snapshot.elements,
    strokes: draft.strokes,
    markedImageId,
  });
  return [
    {
      id: markedImageId,
      name: 'Preview · pencil.jpg',
      mimeType: 'image/jpeg',
      base64Data,
      width: canvas.width,
      height: canvas.height,
      previewAnnotation,
    },
  ];
}

export function usePreviewAnnotation({
  target,
  enabled = true,
  sendCapture,
  cancelCapture,
  beforeCapture,
  resetKey = '',
}: {
  target: PreviewAnnotationTarget | null;
  enabled?: boolean;
  sendCapture: (message: PreviewAnnotationCaptureRequest) => void;
  cancelCapture: (requestId: string) => void;
  beforeCapture: () => void;
  resetKey?: string;
}) {
  const awareness = usePreviewAnnotationAwareness();
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<PreviewSnapshot | null>(null);
  const [strokes, setStrokes] = useState<PreviewAnnotationStroke[]>([]);
  const requestRef = useRef<{ id: string; key: string; timeout: number } | null>(null);
  const generationRef = useRef(0);
  const draftIdRef = useRef<string | null>(null);
  const publishedRef = useRef(false);
  const strokesRef = useRef(strokes);
  const pointerRef = useRef<number | null>(null);
  const targetKey = JSON.stringify(target);
  const key = `${awareness.scopeKey}:${targetKey}:${resetKey}`;
  const keyRef = useRef(key);
  keyRef.current = key;
  const optionsRef = useRef({ target, sendCapture, cancelCapture, beforeCapture, awareness });
  optionsRef.current = { target, sendCapture, cancelCapture, beforeCapture, awareness };

  const cancelWork = useCallback(() => {
    generationRef.current += 1;
    const request = requestRef.current;
    if (request) window.clearTimeout(request.timeout);
    requestRef.current = null;
    if (request) optionsRef.current.cancelCapture(request.id);
    pointerRef.current = null;
  }, []);

  const resetLocal = useCallback(() => {
    cancelWork();
    draftIdRef.current = null;
    publishedRef.current = false;
    strokesRef.current = [];
    setStrokes([]);
    setSnapshot(null);
    setActive(false);
    setBusy(false);
    setError(null);
  }, [cancelWork]);

  const reset = useCallback(() => {
    const draftId = draftIdRef.current;
    resetLocal();
    if (draftId) optionsRef.current.awareness.clearDraft(draftId);
  }, [resetLocal]);

  // Closing a mobile drawer must leave its completed attachment available in chat.
  const detach = useCallback(() => {
    cancelWork();
    const draft = optionsRef.current.awareness.draft;
    if (draft?.id === draftIdRef.current && !draft.images)
      optionsRef.current.awareness.clearDraft(draft.id, draft);
  }, [cancelWork]);

  useEffect(() => {
    reset();
    return detach;
  }, [key, reset, detach]);
  useEffect(() => {
    if (!enabled) {
      detach();
      resetLocal();
    }
  }, [enabled, detach, resetLocal]);
  useEffect(() => {
    if (publishedRef.current && awareness.draft?.id !== draftIdRef.current) reset();
  }, [awareness.draft?.id, reset]);

  const receive = useCallback((message: PreviewAnnotationCaptureResult) => {
    const request = requestRef.current;
    if (!request || message.requestId !== request.id || keyRef.current !== request.key) return;
    window.clearTimeout(request.timeout);
    requestRef.current = null;
    setBusy(false);
    if (message.error) {
      setError(
        typeof message.error === 'string'
          ? message.error.slice(0, 500)
          : 'The preview returned an invalid capture. Try again.'
      );
      return;
    }
    try {
      const captured = parsePreviewSnapshot(message.snapshot);
      draftIdRef.current = crypto.randomUUID();
      setSnapshot(captured);
    } catch {
      setError('The preview returned an invalid capture. Try again.');
    }
  }, []);

  const start = useCallback(async () => {
    if (!enabled || !optionsRef.current.target) return;
    reset();
    setActive(true);
    setBusy(true);
    const requestKey = keyRef.current;
    const id = crypto.randomUUID();
    const timeout = window.setTimeout(() => {
      if (requestRef.current?.id !== id) return;
      cancelWork();
      setBusy(false);
      setError('Capture timed out. Retry once the preview is ready.');
    }, 30_000);
    requestRef.current = { id, key: requestKey, timeout };
    optionsRef.current.beforeCapture();
    try {
      const rendererSource = await loadPreviewAnnotationRenderer();
      if (requestRef.current?.id !== id || keyRef.current !== requestKey) return;
      optionsRef.current.sendCapture({
        source: 'attune-host',
        type: 'annotation-capture',
        requestId: id,
        rendererSource,
      });
    } catch (cause) {
      if (requestRef.current?.id !== id) return;
      window.clearTimeout(timeout);
      requestRef.current = null;
      setBusy(false);
      setError(cause instanceof Error ? cause.message : 'Could not capture this preview.');
    }
  }, [enabled, reset, cancelWork]);

  const publish = useCallback(
    (next: PreviewAnnotationStroke[]) => {
      const id = draftIdRef.current;
      const currentTarget = optionsRef.current.target;
      if (!id || !snapshot || !currentTarget) return;
      const generation = ++generationRef.current;
      const publishedKey = keyRef.current;
      if (!next.length) {
        publishedRef.current = false;
        optionsRef.current.awareness.clearDraft(id);
        return;
      }
      const draft: PreviewAnnotationDraft = {
        id,
        target: currentTarget,
        snapshot,
        strokes: next,
        images: null,
      };
      publishedRef.current = true;
      optionsRef.current.awareness.setDraft(draft);
      void renderImages(draft)
        .then(images => {
          if (generationRef.current === generation && keyRef.current === publishedKey)
            optionsRef.current.awareness.setDraft({ ...draft, images });
        })
        .catch(cause => {
          if (generationRef.current !== generation || keyRef.current !== publishedKey) return;
          const message =
            cause instanceof Error ? cause.message : 'Could not prepare this drawing.';
          setError(message);
          optionsRef.current.awareness.setDraft({ ...draft, error: message });
        });
    },
    [snapshot]
  );

  const replaceStrokes = useCallback((next: PreviewAnnotationStroke[]) => {
    strokesRef.current = next;
    setStrokes(next);
  }, []);
  const beginStroke = useCallback(
    (point: { x: number; y: number }, pointerId: number) => {
      if (!snapshot || pointerRef.current !== null) return false;
      if (
        strokesRef.current.length >= PREVIEW_ANNOTATION_MAX_STROKES ||
        strokesRef.current.reduce((count, stroke) => count + stroke.points.length, 0) >=
          PREVIEW_ANNOTATION_MAX_POINTS
      ) {
        setError('Drawing limit reached. Undo or clear some marks to continue.');
        return false;
      }
      setError(null);
      pointerRef.current = pointerId;
      const next = [
        ...strokesRef.current,
        { points: [point], color: PREVIEW_ANNOTATION_COLOR, width: 4 },
      ];
      replaceStrokes(next);
      // Keep send disabled until pointer-up publishes the finished stroke and marked image.
      const id = draftIdRef.current;
      const currentTarget = optionsRef.current.target;
      if (id && currentTarget) {
        generationRef.current += 1;
        publishedRef.current = true;
        optionsRef.current.awareness.setDraft({
          id,
          target: currentTarget,
          snapshot,
          strokes: next,
          images: null,
        });
      }
      return true;
    },
    [replaceStrokes, snapshot]
  );
  const moveStroke = useCallback(
    (point: { x: number; y: number }, pointerId: number) => {
      if (pointerRef.current !== pointerId) return;
      const current = strokesRef.current;
      if (
        current.reduce((count, stroke) => count + stroke.points.length, 0) >=
        PREVIEW_ANNOTATION_MAX_POINTS
      )
        return;
      const last = current[current.length - 1];
      if (!last) return;
      const previous = last.points[last.points.length - 1];
      if (Math.hypot(point.x - previous.x, point.y - previous.y) < 0.0015) return;
      replaceStrokes([...current.slice(0, -1), { ...last, points: [...last.points, point] }]);
    },
    [replaceStrokes]
  );
  const endStroke = useCallback(
    (pointerId: number) => {
      if (pointerRef.current !== pointerId) return;
      pointerRef.current = null;
      publish(strokesRef.current);
    },
    [publish]
  );
  const undo = useCallback(() => {
    pointerRef.current = null;
    const next = strokesRef.current.slice(0, -1);
    replaceStrokes(next);
    publish(next);
    setError(null);
  }, [publish, replaceStrokes]);
  const clear = useCallback(() => {
    pointerRef.current = null;
    replaceStrokes([]);
    publish([]);
    setError(null);
  }, [publish, replaceStrokes]);
  const toggle = useCallback(() => {
    if (!enabled) return;
    if (active) {
      if (requestRef.current) {
        reset();
        return;
      }
      if (pointerRef.current !== null) endStroke(pointerRef.current);
      setActive(false);
    } else if (snapshot && strokesRef.current.length) {
      optionsRef.current.beforeCapture();
      setActive(true);
    } else {
      const retained = optionsRef.current.awareness.draft;
      const currentTarget = optionsRef.current.target;
      if (
        retained?.images &&
        currentTarget &&
        previewAnnotationTargetsEqual(retained.target, currentTarget)
      ) {
        draftIdRef.current = retained.id;
        publishedRef.current = true;
        setSnapshot(retained.snapshot);
        replaceStrokes(retained.strokes);
        optionsRef.current.beforeCapture();
        setActive(true);
      } else void start();
    }
  }, [active, enabled, snapshot, start, reset, endStroke, replaceStrokes]);
  const pause = useCallback(() => {
    if (requestRef.current) {
      reset();
      return;
    }
    if (pointerRef.current !== null) endStroke(pointerRef.current);
    setActive(false);
  }, [endStroke, reset]);
  return {
    active,
    busy,
    error,
    snapshot,
    strokes,
    receive,
    reset,
    start,
    toggle,
    pause,
    undo,
    clear,
    beginStroke,
    moveStroke,
    endStroke,
  };
}

export type PreviewAnnotationController = ReturnType<typeof usePreviewAnnotation>;
